import { Dispatch, SetStateAction } from "react";
import { Button, cn } from "@tellescope/design-system";
import { DestructiveConfirmDialog, Input } from "@tellescope/ui";
import { formatName } from "@tellescope/utils";
import { ExternalLink, Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { UseRelatedContactsBlockReturn } from "../../../hooks";
import { BlockContent } from "../BlockContent";
import { BlockEmptyState } from "../BlockEmptyState";
import { RelatedContactsListItem } from "./RelatedContactsListItem";
import { useRelatedContactsListView, type UseRelatedContactsListViewArgs } from "./useRelatedContactsListView";

export type RelatedContactsListViewProps = UseRelatedContactsListViewArgs &
  Pick<
    UseRelatedContactsBlockReturn,
    "openAddView" | "editMode" | "expanded" | "toggleExpanded" | "canEditEnduser" | "blockLayout"
  > & {
    setEditMode?: Dispatch<SetStateAction<boolean>>;
    contentClassName?: string;
    onChangeEnduser?: (enduserId: string) => void;
  };

export const RelatedContactsListView = ({
  openAddView,
  editMode,
  expanded,
  toggleExpanded,
  canEditEnduser,
  blockLayout,
  setEditMode,
  contentClassName,
  onChangeEnduser,
  ...listViewArgs
}: RelatedContactsListViewProps) => {
  const { t } = useTranslation("contacts");
  const {
    searchQuery,
    setSearchQuery,
    filteredRelatedContacts,
    hasActiveFilter,
    hasRelationships,
    isLoading,
    selectedContactId,
    setSelectedContactId,
    selectedRelatedContact,
    setShowDeleteConfirmation,
    removeRelatedContact,
    showDeleteConfirmation,
  } = useRelatedContactsListView(listViewArgs);

  return (
    <>
      {!!editMode && (
        <div className="mt-2 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t("Search related contacts...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              autoFocus
            />
          </div>
          {canEditEnduser && (
            <Button
              iconOnly
              variant="ghost"
              size="sm"
              color="muted"
              hoverLabel={t("Add related contact")}
              onClick={openAddView}
            >
              <Plus />
            </Button>
          )}
        </div>
      )}

      <BlockContent
        editMode={editMode}
        setEditMode={canEditEnduser ? setEditMode : undefined}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-col gap-1", contentClassName)}
      >
        {isLoading && hasRelationships ? (
          <BlockEmptyState>{t("Loading related contacts...")}</BlockEmptyState>
        ) : filteredRelatedContacts.length === 0 ? (
          <BlockEmptyState>
            {searchQuery || hasActiveFilter
              ? t("No related contacts found matching your search")
              : t("No related contacts found")}
          </BlockEmptyState>
        ) : (
          filteredRelatedContacts.map(({ contact: relatedContact, relationshipType }) => {
            const isSelected = selectedContactId === relatedContact.id;
            const selectable = editMode && canEditEnduser;
            return (
              <RelatedContactsListItem
                key={relatedContact.id}
                contact={relatedContact}
                selected={isSelected}
                onClick={selectable ? () => setSelectedContactId(isSelected ? null : relatedContact.id) : undefined}
                trailing={relationshipType}
              />
            );
          })
        )}
      </BlockContent>

      {selectedRelatedContact && editMode && canEditEnduser && (
        <div className={cn("flex justify-between items-center pt-2", !onChangeEnduser && "justify-end")}>
          {!!onChangeEnduser && (
            <Button variant="ghost" size="sm" onClick={() => onChangeEnduser(selectedRelatedContact.contact.id)}>
              <ExternalLink />
              {t("Go to related contact")}
            </Button>
          )}
          <Button
            variant="outline"
            className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
            onClick={() => setShowDeleteConfirmation(true)}
            size="sm"
          >
            {t("Remove {{name}}", { name: formatName(selectedRelatedContact.contact) })}
          </Button>
        </div>
      )}

      <DestructiveConfirmDialog
        open={showDeleteConfirmation}
        onOpenChange={(open) => !open && setShowDeleteConfirmation(false)}
        title={t("Remove Related Contact")}
        description={t("Are you sure you want to remove this {{type}} relationship? This action cannot be undone.", {
          type: selectedRelatedContact?.relationshipType,
        })}
        confirmText={t("Remove")}
        onConfirm={removeRelatedContact}
      />
    </>
  );
};
